/**
 * Our own rows of a decision: the one taken and the ones left aside
 * (CU-869fh646w, UX decision D8 and addendum A1/A2).
 *
 * Since this task a cell produces one decision per rate card: the
 * engine gathers every row of ours the card resolves, the winning
 * strategy judges each row's own kind, and "our row" is the cheapest by
 * PVP among the ones it covers. The others travel in
 * `derivation.own_discarded = [{car, pvp, tariff_type, insurance, why}]`
 * with `why` = `dearer` (covered, dearer) or `out_of_scope` (the
 * strategy does not cover that kind). The screen says them in one line
 * under *Our price* — never as extra rows: "our row" is one.
 *
 * Pure: names come from the axis catalogue, money from the caller's
 * formatter, so the line is tested without mounting anything.
 */

const WHY_LABEL = {
	dearer: 'dearer',
	out_of_scope: 'outside this rule’s scope',
}

const asBool = (v) => (v == null ? null : Boolean(Number(v)))
const present = (v) => v !== null && v !== undefined && v !== ''

/** `'Prepaid · Zero excess'` of one row — `—` on an axis it does not carry. */
export function kindLabel(row, names) {
	if (!row) return null
	const tariff = present(row.tariff_type) ? names.prepaidName(asBool(row.tariff_type)) : '—'
	const insurance = present(row.insurance) && Number(row.insurance) !== 0
		? names.insuranceName(row.insurance) : '—'
	return `${tariff} · ${insurance}`
}

/**
 * For a row the strategy does not cover, the value of the axis that
 * leaves it out — the kind IS the reason. Both axes when both differ
 * from the row taken (or when nothing differs, which would be a row
 * the engine should have covered: say both rather than nothing).
 */
function outOfScopeKind(row, own, names) {
	const tariffDiffers = own == null || asBool(row.tariff_type) !== asBool(own.tariff_type)
	const insuranceDiffers = own == null || String(row.insurance ?? '') !== String(own.insurance ?? '')
	const parts = []
	if (tariffDiffers || !insuranceDiffers) parts.push(present(row.tariff_type) ? names.prepaidName(asBool(row.tariff_type)) : 'no tariff type recorded')
	if (insuranceDiffers || !tariffDiffers) parts.push(present(row.insurance) && Number(row.insurance) !== 0 ? names.insuranceName(row.insurance) : 'no insurance recorded')
	return parts.join(' · ')
}

/**
 * The one line, or `null` when nothing was discarded (like `excludedCopy`
 * at zero).
 *
 *   { lead: '3 more of our rows on this site:',
 *     items: [{ text: 'Seat Arona 224.00 € (Non prepaid — outside this rule’s scope)',
 *               title: 'Non prepaid · Zero excess' }, …] }
 *
 * Rows in ascending PVP; totals for the stay like the whole block. The
 * reason goes in brackets only when the rows do not all share it; an
 * `out_of_scope` row names the axis value that leaves it out; a `why`
 * the screen does not know is printed as is, never hidden.
 */
export function discardedCopy(discarded, own, days, { prepaidName, insuranceName, money }) {
	const rows = (Array.isArray(discarded) ? discarded : []).filter(Boolean)
	if (!rows.length) return null
	const names = { prepaidName, insuranceName }
	const pvp = (r) => (r.pvp == null ? Infinity : Number(r.pvp))
	const sorted = [...rows].sort((a, b) => pvp(a) - pvp(b))
	const whys = [...new Set(sorted.map(r => String(r.why || '')))]
	const shared = whys.length === 1 ? whys[0] : null
	const total = (r) => (r.pvp == null ? null : (Number(days) > 0 ? Number(r.pvp) * Number(days) : Number(r.pvp)))
	const n = sorted.length
	const lead = `${n} more of our rows on this site`
		+ (shared ? `, ${WHY_LABEL[shared] || shared}:` : ':')
	const items = sorted.map(r => {
		const amount = money(total(r))
		let bracket = null
		if (r.why === 'out_of_scope') {
			const kind = outOfScopeKind(r, own, names)
			bracket = shared ? kind : `${kind} — ${WHY_LABEL.out_of_scope}`
		} else if (!shared) {
			bracket = WHY_LABEL[r.why] || String(r.why || '')
		}
		return {
			text: `${r.car || 'our row'} ${amount}${bracket ? ` (${bracket})` : ''}`,
			title: kindLabel(r, names),
		}
	})
	return { lead, items }
}

/**
 * The reasons a `no_price_change` carries when the row taken has no
 * kind recorded on an axis that still filters (plan § 1.2): nothing can
 * be judged like with like, so nothing was priced and the market was
 * not read. The row sub-label sits in the Event column, where
 * `capped_by` and the spread label already live, because the reason is
 * in our row and not in the market and the operator has to see it
 * without opening (addendum A2).
 */
export const OWN_KIND_ROW_LABEL = {
	own_kind_unknown_insurance: 'Own row: insurance not recorded',
	own_kind_unknown_tariff:    'Own row: tariff type not recorded',
	own_kind_unknown_both:      'Own row: kind not recorded',
}

export const ownKindRowLabel = (reason) => OWN_KIND_ROW_LABEL[reason] || null
export const isOwnKindUnknown = (reason) => Object.prototype.hasOwnProperty.call(OWN_KIND_ROW_LABEL, String(reason || ''))
